/**
 * HSE Compliance — mandatory training tracking for commercial drivers.
 *
 * 01/06/2026 — Phase 4 (FCO) + Phase 5 (full training matrix).
 *
 * Training catalogue (each has a validity window in game-days and a weight
 * that contributes to the company HSE score):
 *
 *   ┌─────────┬──────────────────────────────────┬───────────┬────────┬───────────────┐
 *   │  Key    │  Label (FR)                      │  Validity │ Weight │  Applies to   │
 *   ├─────────┼──────────────────────────────────┼───────────┼────────┼───────────────┤
 *   │  fco    │ Formation Continue Obligatoire   │  1800 d   │   2    │ All drivers   │
 *   │  medical│ Visite médicale d'aptitude       │   360 d   │   2    │ All drivers   │
 *   │  adr    │ Certification ADR (rappel)       │  1800 d   │   1    │ ADR specialty │
 *   │  caces  │ CACES R489/R372 (recyclage)      │  1800 d   │   1    │ BTP specialty │
 *   └─────────┴──────────────────────────────────┴───────────┴────────┴───────────────┘
 *
 * Status returned per training:
 *   - 'compliant' : daysRemaining > EXPIRING_WINDOW_DAYS
 *   - 'expiring'  : 0 <= daysRemaining <= EXPIRING_WINDOW_DAYS
 *   - 'expired'   : daysRemaining < 0
 *   - 'unknown'   : no anchor (no hiredAt + no training entry, rare)
 *   - 'na'        : training not applicable for that driver (filtered out)
 *
 * Soft blocking (per user spec, 01/06/2026 — Phase 5):
 *   - Medical expired → driver cannot be assigned to ANY vehicle.
 *   - ADR expired AND vehicle requires ADR → driver cannot drive THAT vehicle.
 *   - FCO and CACES expired → narrative only (Claire mémo) — no hard block.
 *
 * Pure functions — safe to call from React selectors.
 */

export const EXPIRING_WINDOW_DAYS = 60;

// Legacy alias kept for backward-compat with Phase 4 tests / consumers.
export const FCO_VALIDITY_DAYS = 1800;

/**
 * Training catalogue. New trainings can be added by extending this map
 * without touching the helper logic.
 */
export const HSE_TRAININGS = {
  fco: {
    key: 'fco',
    labelFr: 'FCO — Formation Continue Obligatoire',
    labelEn: 'FCO — Mandatory Continuous Training',
    validityDays: 1800,
    weight: 2,
    applicabilityKind: 'all',
    blocking: false,
    cost: 600,
    days: 2,
  },
  medical: {
    key: 'medical',
    labelFr: "Visite médicale d'aptitude",
    labelEn: 'Annual medical fitness check',
    validityDays: 360,
    weight: 2,
    applicabilityKind: 'all',
    blocking: 'global', // blocks assignment to any vehicle when expired
    cost: 200,
    days: 1,
  },
  adr: {
    key: 'adr',
    labelFr: 'Certification ADR (rappel)',
    labelEn: 'ADR certification (refresh)',
    validityDays: 1800,
    weight: 1,
    applicabilityKind: 'specialty',
    specialtyMatch: ['ADR/Produits sensibles'],
    blocking: 'vehicleAdr', // blocks ADR vehicles only
    cost: 1200,
    days: 3,
  },
  caces: {
    key: 'caces',
    labelFr: 'CACES R489 / R372 (recyclage)',
    labelEn: 'CACES R489 / R372 (refresh)',
    validityDays: 1800,
    weight: 1,
    applicabilityKind: 'specialty',
    specialtyMatch: ['BTP/Matériaux/Engins TP'],
    blocking: false,
    cost: 800,
    days: 4,
  },
};

const HSE_TRAINING_KEYS = Object.keys(HSE_TRAININGS);

// eslint-disable-next-line global-require
const { matchSpec } = require('../../utils/specialtyMatcher');

function getDriverSpecialtiesSet(driver) {
  const s = new Set();
  if (driver?.specialty) s.add(driver.specialty);
  if (driver?.specialty2) s.add(driver.specialty2);
  for (const x of (driver?.extraSpecialties || [])) s.add(x);
  return s;
}

/**
 * Returns true if a given training applies to the driver based on the
 * catalogue's `applicabilityKind` rule.
 *
 * 04/02/2026 — Phase F3 : `specialtyMatch` est comparé via matchSpec()
 * pour tolérer les anciens libellés (ex. 'ADR' ↔ 'ADR/Produits sensibles').
 */
export function isTrainingApplicable(trainingKey, driver) {
  const spec = HSE_TRAININGS[trainingKey];
  if (!spec || !driver) return false;
  if (spec.applicabilityKind === 'all') return true;
  if (spec.applicabilityKind === 'specialty') {
    const have = Array.from(getDriverSpecialtiesSet(driver));
    return (spec.specialtyMatch || []).some(target =>
      have.some(driverSpec => matchSpec(driverSpec, target))
    );
  }
  return false;
}

/**
 * Anchor day for a given training: latest of hiredAt and any training entry
 * of the matching type. Returns null when no anchor exists.
 */
export function getTrainingAnchorDay(driver, trainingKey) {
  if (!driver) return null;
  const candidates = [];
  if (typeof driver.hiredAt === 'number') candidates.push(driver.hiredAt);
  const trainings = Array.isArray(driver.trainings) ? driver.trainings : [];
  for (const t of trainings) {
    if (t && t.type === trainingKey && typeof t.startDay === 'number') {
      candidates.push(t.startDay);
    }
  }
  if (candidates.length === 0) return null;
  return Math.max(...candidates);
}

/**
 * Status for one (driver, training) pair.
 */
export function getDriverTrainingStatus(driver, trainingKey, totalDays = 0) {
  const spec = HSE_TRAININGS[trainingKey];
  if (!spec) return { status: 'unknown', expiresOn: null, daysRemaining: null };
  if (!isTrainingApplicable(trainingKey, driver)) {
    return { status: 'na', expiresOn: null, daysRemaining: null, applicable: false };
  }
  const anchor = getTrainingAnchorDay(driver, trainingKey);
  if (anchor === null) {
    return { status: 'unknown', expiresOn: null, daysRemaining: null, applicable: true };
  }
  const expiresOn = anchor + spec.validityDays;
  const daysRemaining = expiresOn - totalDays;
  let status;
  if (daysRemaining < 0) status = 'expired';
  else if (daysRemaining <= EXPIRING_WINDOW_DAYS) status = 'expiring';
  else status = 'compliant';
  return { status, expiresOn, daysRemaining, applicable: true, anchorDay: anchor };
}

// ── Phase 4 backward-compat (FCO-only) ───────────────────────────────────
export function getFcoAnchorDay(driver) {
  return getTrainingAnchorDay(driver, 'fco');
}
export function getDriverFcoStatus(driver, totalDays = 0) {
  const result = getDriverTrainingStatus(driver, 'fco', totalDays);
  if (result.applicable === false) return result;
  // Strip `applicable` flag for legacy callers that expect the old shape.
  const { applicable: _drop, ...rest } = result;
  void _drop;
  return { ...rest, anchorDay: result.anchorDay !== undefined ? result.anchorDay : getFcoAnchorDay(driver) };
}

/**
 * Per-driver detail across all HSE trainings, with overall compliance score
 * weighted by the catalogue weights and limited to APPLICABLE trainings.
 *
 * Returns:
 *   {
 *     driverId, name,
 *     trainings: { fco: {...}, medical: {...}, adr?: {...}, caces?: {...} },
 *     weightedScore: 0-100,
 *     worstStatus: 'compliant'|'expiring'|'expired'|'unknown'|'na',
 *   }
 */
export function getDriverHseDetail(driver, totalDays = 0) {
  const trainings = {};
  let totalWeight = 0;
  let weightedSum = 0;
  let worst = 'compliant';
  const RANK = { compliant: 0, unknown: 1, expiring: 2, expired: 3 };
  for (const key of HSE_TRAINING_KEYS) {
    const tStatus = getDriverTrainingStatus(driver, key, totalDays);
    trainings[key] = tStatus;
    if (tStatus.applicable === false) continue;
    const w = HSE_TRAININGS[key].weight;
    totalWeight += w;
    if (tStatus.status === 'compliant') weightedSum += w * 1;
    else if (tStatus.status === 'expiring') weightedSum += w * 0.5;
    else if (tStatus.status === 'unknown') weightedSum += w * 0.5;
    // expired contributes 0
    if ((RANK[tStatus.status] || 0) > (RANK[worst] || 0)) worst = tStatus.status;
  }
  const weightedScore = totalWeight === 0
    ? 100
    : Math.round((weightedSum / totalWeight) * 100);
  return {
    driverId: driver?.id,
    name: driver?.name,
    trainings,
    weightedScore,
    worstStatus: worst,
  };
}

/**
 * Company-wide compliance aggregate. Used by the RSEDashboard widget AND
 * the coreSlice.nextDay alert scan.
 */
export function getCompanyHseCompliance(drivers, totalDays = 0) {
  const list = Array.isArray(drivers) ? drivers : [];
  if (list.length === 0) {
    return {
      overall: 100,
      statuses: [],
      counts: { compliant: 0, expiring: 0, expired: 0, unknown: 0 },
      details: [],
    };
  }
  // PER-DRIVER worst-case status (backward-compat: counts + statuses).
  const statuses = [];
  const details = [];
  const counts = { compliant: 0, expiring: 0, expired: 0, unknown: 0 };
  let weightedTotal = 0;
  let weightedCount = 0;
  for (const d of list) {
    const detail = getDriverHseDetail(d, totalDays);
    details.push(detail);
    // Per-driver worst status across applicable trainings.
    let driverStatus = detail.worstStatus;
    if (driverStatus === 'na') driverStatus = 'compliant'; // no applicable training → compliant
    counts[driverStatus] = (counts[driverStatus] || 0) + 1;
    // Statuses entry retains FCO summary for Phase 4 back-compat consumers.
    const fco = detail.trainings.fco || {};
    statuses.push({
      driverId: d.id,
      name: d.name,
      status: driverStatus,
      daysRemaining: fco.daysRemaining ?? null,
      expiresOn: fco.expiresOn ?? null,
    });
    weightedTotal += detail.weightedScore;
    weightedCount += 1;
  }
  const overall = weightedCount === 0 ? 100 : Math.round(weightedTotal / weightedCount);
  return { overall, statuses, counts, details };
}

// ── Blocking helpers (used by contractSlice.acceptContract + UI) ─────────

/**
 * Check whether assigning `driver` to `vehicle` is blocked by HSE
 * compliance. Returns `{ blocked: bool, reason?: string, trainingKey?, expiresOn? }`.
 *
 * Rules:
 *   • ADR expired AND vehicle requires ADR → blocked.
 *
 * 02/06/2026 — Medical removed from the blocking list per user feedback :
 *   « les visites médicales ne soient évoqués qu'à titre informatif il
 *     faut retirer l'indisponibilité des chauffeurs et les actions
 *     associés ».
 * The medical training status is still tracked & surfaced on the RSE
 * dashboard (informative), but it no longer blocks vehicle assignments.
 *
 * "Vehicle requires ADR" is detected via specialties (`Spécialisé` /
 * 'ADR/Produits sensibles') OR license === 'ADR/Produits sensibles'. We use a conservative heuristic: if the
 * vehicle's specialties array contains 'ADR/Produits sensibles' or the vehicle requires an
 * 'ADR/Produits sensibles' license, it's an ADR vehicle.
 */
export function checkAssignmentHseBlock(driver, vehicle, totalDays = 0) {
  if (!driver) return { blocked: false };
  // 02/06/2026 — Medical block removed (was : if expired → always blocked).
  // ADR — only if vehicle is ADR.
  const isAdrVehicle = isVehicleAdr(vehicle);
  if (isAdrVehicle) {
    const adr = getDriverTrainingStatus(driver, 'adr', totalDays);
    if (adr.applicable && adr.status === 'expired') {
      return { blocked: true, reason: 'adr_expired', trainingKey: 'adr', expiresOn: adr.expiresOn };
    }
  }
  return { blocked: false };
}

function isVehicleAdr(vehicle) {
  if (!vehicle) return false;
  const specs = vehicle.specialties || [];
  // Tolère les anciens libellés ('ADR') et le libellé canonique.
  // eslint-disable-next-line global-require
  const { matchSpec } = require('../../utils/specialtyMatcher');
  return specs.some((s) => matchSpec(s, 'ADR/Produits sensibles'));
}
